import { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { flushSync } from 'react-dom';

const STORAGE_KEY = 'biometric-theme';
const ThemeContext = createContext(null);

function getInitialTheme() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {}
  if (window.matchMedia('(prefers-color-scheme: light)').matches) return 'light';
  return 'dark';
}

function prefersReducedMotion() {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch { return false; }
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(getInitialTheme);
  const [lockedTheme, setLockedTheme] = useState(null);
  const userPreference = useRef(true);
  const isAnimating = useRef(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) userPreference.current = false;
  }, []);

  const setTheme = useCallback((next) => {
    userPreference.current = true;
    setThemeState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch {}
  }, []);

  const lockTheme = useCallback((mode) => {
    setLockedTheme(mode);
  }, []);

  const unlockTheme = useCallback(() => {
    setLockedTheme(null);
  }, []);

  const toggleTheme = useCallback((originX, originY) => {
    if (lockedTheme) return;
    if (isAnimating.current) return;
    isAnimating.current = true;

    const next = theme === 'dark' ? 'light' : 'dark';
    const x = originX ?? window.innerWidth / 2;
    const y = originY ?? window.innerHeight / 2;

    document.documentElement.style.setProperty('--toggle-x', `${x}px`);
    document.documentElement.style.setProperty('--toggle-y', `${y}px`);

    const doUpdate = () => {
      userPreference.current = true;
      setThemeState(next);
      try { localStorage.setItem(STORAGE_KEY, next); } catch {}
    };

    const done = () => { isAnimating.current = false; };

    if (document.startViewTransition && !prefersReducedMotion()) {
      document.documentElement.classList.add('no-transitions');
      try {
        const vt = document.startViewTransition(() => {
          flushSync(doUpdate);
        });
        vt.ready.then(() => {
          document.documentElement.classList.remove('no-transitions');
        }).catch(() => {
          document.documentElement.classList.remove('no-transitions');
          done();
        });
        vt.finished.then(done).catch(done);
        return;
      } catch {
        document.documentElement.classList.remove('no-transitions');
      }
    }

    doUpdate();
    done();
  }, [theme, lockedTheme]);

  const effectiveTheme = lockedTheme || theme;

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme-mode', effectiveTheme);
    root.style.colorScheme = effectiveTheme;
  }, [effectiveTheme]);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: light)');
    const handler = (e) => {
      if (!userPreference.current && !lockedTheme) {
        setThemeState(e.matches ? 'light' : 'dark');
      }
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [lockedTheme]);

  const value = useMemo(
    () => ({
      theme: effectiveTheme,
      preferredTheme: theme,
      setTheme,
      toggleTheme,
      lockTheme,
      unlockTheme,
      isThemeLocked: lockedTheme != null,
    }),
    [effectiveTheme, theme, setTheme, toggleTheme, lockTheme, unlockTheme, lockedTheme],
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
}
